/**
 * @file Focused auth card for `/authorize`.
 * @module @labcraft/lab/ui/components/auth-card
 *
 * Single auth surface. Sign-up and sign-in collapse here — the
 * backend resolves new vs returning via `findOrLinkByClaims`; the
 * gateway's `resolvePostAuthRedirect` decides where to drop the
 * user based on `status` + `profileCompletedAt`. Legacy
 * URLs (`/signin`) redirect to `/authorize` at the router layer.
 *
 * Marketing surfaces (landing, manifesto) live on
 * `@labcraft/www` and close on a cross-host Sign-in CTA that
 * navigates to this card on `lab.labcraft.dev/authorize`. No sibling
 * marketing-styled card exists in the lab bundle.
 */

import { useState, type FormEvent, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { useClient } from '@labcraft/core/frontend/react'
import type { Lab } from '../../client/index.ts'
import { useSigninWithEmailMutation } from '../hooks/use_signin_with_email.ts'
import { GithubLogo } from '@labcraft/ui'
import { GoogleLogo } from '@labcraft/ui'
import styles from './auth_card.module.css'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Props for {@link AuthCard}.
 *
 * @property next     Optional post-callback redirect target —
 *                    forwarded to every provider. The gateway
 *                    sanitizes it server-side; callers pass
 *                    whatever the surrounding page wanted (e.g.
 *                    `/courses` from a chapter CTA).
 * @property expired  When true, renders a "your session timed out"
 *                    notice above the form. Used by
 *                    `/authorize?expired=1` (the SessionExpiredWatcher
 *                    target).
 */
export type AuthCardProps = {
  next?: string | null
  expired?: boolean
}

export function AuthCard(props: AuthCardProps): JSX.Element {
  const app = useClient<Lab>()
  const navigate = useNavigate()
  const signinWithEmail = useSigninWithEmailMutation()
  const next = props.next ?? null

  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)

  const submitting = signinWithEmail.isPending
  const canSubmit = !submitting && EMAIL_REGEX.test(email.trim())

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!canSubmit) return
    setError(null)
    try {
      await signinWithEmail.mutateAsync({
        email: email.trim(),
        ...(next && { next }),
      })
      const sentParams = new URLSearchParams({
        email: email.trim(),
        purpose: 'signin',
      })
      if (next) sentParams.set('next', next)
      navigate(`/auth/sent?${sentParams.toString()}`, { replace: true })
    } catch {
      setError('Something went wrong. Please try again in a moment.')
    }
  }

  return (
    <section className={styles.card}>
      <h1 className={styles.title}>Pull a chair up to the lab.</h1>
      <p className={styles.lede}>
        LabCraft is open as a public beta. Pick whichever sign-in path suits
        you, then start a lab or pick up where you left off.
      </p>

      {props.expired && (
        <div className={styles.noticeBanner} role="status">
          Your session timed out. Sign back in and you&apos;ll land right where
          you left off.
        </div>
      )}

      {error && <div className={styles.errorBanner}>{error}</div>}

      <p className={styles.legalNote}>
        By continuing, you confirm that you are at least 18, agree to our{' '}
        <a
          href="https://www.labcraft.dev/terms"
          target="_blank"
          rel="noopener noreferrer">
          Terms
        </a>{' '}
        and acknowledge our{' '}
        <a
          href="https://www.labcraft.dev/privacy"
          target="_blank"
          rel="noopener noreferrer">
          Privacy Policy
        </a>
        .
      </p>

      <div className={styles.ssoStack}>
        <button
          className={styles.ssoButton}
          type="button"
          onClick={() => app.account.signinWithGithub(next ?? undefined)}
          disabled={submitting}>
          <GithubLogo className={styles.ssoLogo} />
          Continue with GitHub
        </button>

        <button
          className={styles.ssoButton}
          type="button"
          onClick={() => app.account.signinWithGoogle(next ?? undefined)}
          disabled={submitting}>
          <GoogleLogo className={styles.ssoLogo} />
          Continue with Google
        </button>
      </div>

      <div className={styles.ssoDivider}>or</div>

      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <label className={styles.field}>
          <span className={styles.label}>Email</span>
          <input
            className={styles.input}
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>
        <button className={styles.cta} type="submit" disabled={!canSubmit}>
          {submitting ? 'Sending…' : 'Email me a link →'}
        </button>
      </form>

      <p className={styles.fineprint}>
        Been here before? The same buttons sign you back in.
      </p>
    </section>
  )
}
